/**
 * Shared ApexCharts styling for the Aux Sensor tab.
 *
 * The four plots in this folder previously each carried their own copy of the
 * toolbar/zoom/grid config and their own colour literals, which had already
 * drifted apart (three different greys for "excluded"). One home for both.
 */
import { ApexOptions } from 'apexcharts';

/** Brand accent, matching `cirrusBlue-400` / `--cirrus-blue-400` in the theme. */
export const AUX_ACCENT = '#03365D';

export const AUX_COLORS = {
  /**
   * Series palette, in order, for BOTH cards that colour a list of series by
   * position — `AuxSensorPlot`'s same-type sensor instances and
   * `AuxComparisonPlot`'s compared pairs. Each indexes it modulo its length,
   * so its length is part of the contract: growing it from three entries
   * stopped a fourth comparison series wrapping back onto the first one's
   * blue, and any further change recolours both cards.
   *
   * Every colour that means something specific elsewhere on this tab is
   * deliberately absent, because the cards sit within a scroll of each other:
   * red belongs to `fit`, the violet below to `ratio`, and the greys to the
   * raw/excluded states. A series wearing one of those reads as that thing
   * instead — a fourth instance in `ratio`'s violet was drawn in the
   * slip-ratio colour two cards above its own legend. A fuchsia `#D946EF`
   * came out again for the same reason once the list was shared: near enough
   * `ratio` in hue that on the comparison card, which sits directly under the
   * slip-ratio pair, it read as the slip-ratio trace.
   */
  series: ['#3B82F6', '#10B981', '#F59E0B', '#06B6D4', '#EC4899', '#84CC16'],
  /**
   * `markersOnly` series (raw measured samples), so the points read as data
   * underneath the coloured model lines. Line series keep `series` above, in
   * order, regardless of where the marker series sits in the list.
   */
  markersOnly: '#6B7280',
  /** Slip-ratio traces. */
  ratio: '#8B5CF6',
  /** The 1:1 / ratio = 1 reference lines. */
  reference: '#9CA3AF',
  /** Least-squares fit line. */
  fit: '#EF4444',
  /** Dropped by the fit window — mid grey. */
  outsideFit: '#9CA3AF',
  /** Dropped by the min-speed threshold — light grey. */
  masked: '#D1D5DB',
};

/**
 * Chart/toolbar/grid/legend config every plot on the tab shares. Spread it and
 * override what differs, rather than restating the toolbar tool list five times.
 */
export function auxBaseOptions(height: number): ApexOptions {
  return {
    chart: {
      height,
      animations: { enabled: false },
      toolbar: {
        show: true,
        tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      },
      zoom: { enabled: true, type: 'xy' },
    },
    grid: { borderColor: '#E5E7EB', strokeDashArray: 5 },
    legend: { position: 'top', horizontalAlign: 'left' },
  };
}

/**
 * Pins the hover tooltip to one corner instead of letting it follow the
 * cursor. A box that moves with the mouse covers the very samples being
 * read, and on a dense 20 Hz trace it flickers between sides of the
 * pointer; parked in a corner the numbers stay in one place to read.
 *
 * Apex measures the corner from the whole SVG, so the bottom edge lands on
 * the x-axis labels — `offsetY` lifts it back inside the plot area.
 */
export const AUX_FIXED_TOOLTIP = {
  enabled: true,
  position: 'bottomRight' as const,
  offsetX: 0,
  offsetY: -34,
};

/**
 * Default y-axis window for the slip-ratio plots. Ratios of interest sit close to
 * 1; without a fixed range a handful of low-speed outliers stretch the axis and
 * flatten the signal. Zoom/pan still reaches anything outside the window.
 */
export const RATIO_Y_AXIS_RANGE = { min: 0.8, max: 1.2 };

/** Fallback wheel-pair name, when the run does not name the two wheels. */
export const DEFAULT_PAIR_LABEL = 'Wheel 1 / Wheel 2';

/**
 * The slip-ratio y-axis, shared by the time and force plots so the two cannot
 * drift apart in title wording or tick precision.
 */
export function ratioYAxis(pairLabel: string) {
  return {
    ...RATIO_Y_AXIS_RANGE,
    title: { text: `Slip Ratio — ${pairLabel}`, style: { fontSize: '13px', fontWeight: 600 } },
    labels: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
  };
}

/** The `ratio = 1` (sensor agreement) marker shared by both slip-ratio plots. */
export const RATIO_ONE_ANNOTATION = {
  yaxis: [{
    y: 1,
    borderColor: AUX_COLORS.reference,
    strokeDashArray: 6,
    label: { text: 'ratio = 1', style: { color: '#6B7280', background: '#F9FAFB' } },
  }],
};

/**
 * One plotted series plus its per-series styling. Apex takes styling as parallel
 * arrays rather than per-series properties, so the descriptors are declared
 * together and fanned out by `styledApexSeries` — keeping colour, marker size and
 * stroke width from drifting out of alignment with the series they belong to.
 */
export interface AuxPlotSeriesSpec {
  name: string;
  type: 'scatter' | 'line';
  data: (number | null)[][];
  color: string;
  markerSize: number;
  strokeWidth: number;
  dashArray?: number;
}

/** Scatter-point defaults — every plot on the tab draws points the same size. */
export function scatterSpec(
  name: string,
  data: (number | null)[][],
  color: string,
): AuxPlotSeriesSpec {
  return { name, type: 'scatter', data, color, markerSize: 3, strokeWidth: 0 };
}

/** Line defaults; `dashArray` > 0 gives the dashed reference-line look. */
export function lineSpec(
  name: string,
  data: (number | null)[][],
  color: string,
  dashArray = 0,
): AuxPlotSeriesSpec {
  return { name, type: 'line', data, color, markerSize: 0, strokeWidth: 2, dashArray };
}

/** Fan a descriptor list out into the parallel arrays Apex expects. */
export function styledApexSeries(specs: AuxPlotSeriesSpec[]): {
  series: { name: string; type: string; data: (number | null)[][] }[];
  options: ApexOptions;
} {
  return {
    series: specs.map((s) => ({ name: s.name, type: s.type, data: s.data })),
    options: {
      colors: specs.map((s) => s.color),
      markers: { size: specs.map((s) => s.markerSize), fillOpacity: 0.5, strokeWidth: 0 },
      stroke: {
        width: specs.map((s) => s.strokeWidth),
        dashArray: specs.map((s) => s.dashArray ?? 0),
        curve: 'straight',
      },
    },
  };
}

/** The slice of the ApexCharts window object the label formatters read. */
interface ApexAxisWindow {
  globals?: {
    xAxisScale?: { result?: number[] };
    yAxisScale?: { result?: number[] }[];
    minX?: number;
    maxX?: number;
    xTickAmount?: number;
  };
}

/**
 * ApexCharts hands the chart window to a formatter in a different place on
 * each axis — third argument and bare on the y-axis, third and wrapped in
 * `{ w }` on the x-axis, second and wrapped from the tooltip path. Rather
 * than one formatter per calling convention, pick it out of whichever
 * argument carries it.
 */
function chartWindow(args: unknown[]): ApexAxisWindow | undefined {
  for (let i = 0; i < args.length; i += 1) {
    const arg = args[i] as { globals?: unknown; w?: { globals?: unknown } } | null;
    if (arg && typeof arg === 'object') {
      if (arg.globals) return arg as ApexAxisWindow;
      if (arg.w?.globals) return arg.w as ApexAxisWindow;
    }
  }
  return undefined;
}

/**
 * Decimal places a tick step needs to stay distinct: 1 second needs none,
 * 0.5 needs one, 0.05 needs two. Capped at three, below which the label row
 * is wider than the information in it.
 *
 * The epsilon keeps a step that is only 0.1 after float error (0.09999…)
 * from asking for a decimal it does not need.
 */
function decimalsForStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  return Math.min(3, Math.max(0, Math.ceil(-Math.log10(step) - 1e-9)));
}

/** Decimals implied by an axis tick list, or null when it has under two ticks. */
function decimalsForTicks(ticks: number[] | undefined): number | null {
  if (!ticks || ticks.length < 2) return null;
  return decimalsForStep(Math.abs(ticks[1] - ticks[0]));
}

/**
 * Numeric x-axis label with just enough decimals for the current zoom level,
 * plus an optional unit suffix. A fixed `toFixed(0)` printed the same rounded
 * value on every tick once a zoom took the window below one unit. The ticks
 * come from the chart's own x scale, which is recomputed on zoom, so the label
 * precision follows the zoom.
 *
 * Pairs with leaving `xaxis.decimalsInFloat` unset. That option is not a
 * formatting one — Apex rounds the tick VALUES it generates to it — so
 * `decimalsInFloat: 0` holds the axis to whole units and re-creates the
 * duplicate-tick problem this formatter exists to solve, however the labels
 * are then printed. Whole-unit ticks at the default zoom are given up for
 * distinct ticks at every zoom.
 */
export function xAxisLabel(suffix = '') {
  return (value: string | number, ...args: unknown[]): string => {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(num)) return '';
    const w = chartWindow(args);
    let decimals = decimalsForTicks(w?.globals?.xAxisScale?.result);
    if (decimals === null) {
      const { minX, maxX, xTickAmount } = w?.globals ?? {};
      decimals = minX != null && maxX != null
        ? decimalsForStep((maxX - minX) / (xTickAmount || 10)) : 0;
    }
    return `${num.toFixed(decimals)}${suffix}`;
  };
}

/** Relative-time x-axis label — `xAxisLabel` in seconds. */
export const timeAxisLabel = xAxisLabel('s');

/**
 * Value (y) axis label whose decimals likewise come from the tick step, so
 * a narrow window — a yaw trace covering a tenth of a degree, a zoom into a
 * flat stretch of speed — stops printing the same number on every tick.
 *
 * `axisIndex` is the position of this axis in the chart's `yaxis` array,
 * which is how ApexCharts keys the computed scales; `minDecimals` holds the
 * axis at its usual precision when the ticks are coarse enough not to need
 * any extra.
 */
export function valueAxisLabel(axisIndex = 0, minDecimals = 1) {
  return (value: number, ...args: unknown[]): string => {
    if (value == null || !Number.isFinite(value)) return '';
    const w = chartWindow(args);
    const decimals = decimalsForTicks(w?.globals?.yAxisScale?.[axisIndex]?.result);
    return value.toFixed(Math.max(minDecimals, decimals ?? 0));
  };
}
